import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { CASHIER_AUTH_TOKEN_CHANGED_EVENT, STORAGE_KEYS } from '../constants';

export type CashierProfile = {
  id: string;
  shopId: string;
  username: string;
  status: string;
  lastLoginAt?: string | null;
};

interface AuthState {
  cashier: CashierProfile | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (cashier: CashierProfile, token: string) => void;
  logout: () => void;
}

const CashierAuthContext = createContext<AuthContextValue | undefined>(undefined);

export function CashierAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    cashier: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.CASHIER_AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.CASHIER_DATA);
    setState({
      cashier: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(CASHIER_AUTH_TOKEN_CHANGED_EVENT));
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEYS.CASHIER_AUTH_TOKEN);
    const raw = localStorage.getItem(STORAGE_KEYS.CASHIER_DATA);
    if (token && raw) {
      try {
        const cashier = JSON.parse(raw) as CashierProfile;
        setState({
          cashier,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
        return;
      } catch {
        logout();
        return;
      }
    }
    setState((p) => ({ ...p, isLoading: false }));
  }, [logout]);

  const login = useCallback((cashier: CashierProfile, token: string) => {
    localStorage.setItem(STORAGE_KEYS.CASHIER_AUTH_TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.CASHIER_DATA, JSON.stringify(cashier));
    setState({
      cashier,
      token,
      isAuthenticated: true,
      isLoading: false,
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(CASHIER_AUTH_TOKEN_CHANGED_EVENT));
    }
  }, []);

  useEffect(() => {
    const onUnauthorized = () => logout();
    window.addEventListener('cashier-auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('cashier-auth:unauthorized', onUnauthorized);
  }, [logout]);

  return (
    <CashierAuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </CashierAuthContext.Provider>
  );
}

export function useCashierAuth(): AuthContextValue {
  const ctx = useContext(CashierAuthContext);
  if (!ctx) throw new Error('useCashierAuth must be used within CashierAuthProvider');
  return ctx;
}

